import {Record, Map, List} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';

import * as Actions from './actions';
import type * as Types from './types';

const warningsToShow = 5;

interface BatchInvoicingStateProps {
    institutionId: string | null;
    updatingBillPayerIds: boolean;
    updatingInvoicePreviews: boolean;
    didFetchBillPayerIds: boolean;
    didFetchInvoicePreviews: boolean;
    useGraphQl: boolean;
    sendingInvoices: boolean;
    invoices: Map<string, Types.Invoice[]>;
    billPayerIds: List<string>;
    previewInvoicesMap: Map<string, Types.PreviewInvoice>;
    selectedBillPayers: Map<string, boolean>;
    warningsToShow: number;
    warnings: List<Types.Warning>;
    behaviors: BehaviorsCollection;
}

export class BatchInvoicing extends Record<BatchInvoicingStateProps>({
    institutionId: null,
    updatingBillPayerIds: false,
    updatingInvoicePreviews: false,
    didFetchBillPayerIds: false,
    didFetchInvoicePreviews: false,
    useGraphQl: false,
    sendingInvoices: false,
    invoices: Map(),
    billPayerIds: List(),
    previewInvoicesMap: Map(),
    selectedBillPayers: Map(), // Map<BillPayerId: string, boolean>
    warningsToShow,
    warnings: List(),
    behaviors: new BehaviorsCollection([]),
}) {}

const getSelectedBillPayerIdsFromPreview = (
    selectedBillPayers: Map<string, boolean>,
    previews: any,
    isBatchInvoicingLimited: boolean,
) => {
    /*
        When updating the selected bill payers we account for the already selected ones.
        We reduce the list of new bill payer IDs and check if there's a defined value in the current map
        otherwise we default to true when the batch invoicing limitation is not 'on' and false if the 
        it is limited
     */
    const selectedBillPayerIdsFromPreview = previews
        .filter(preview => !preview.error)
        .keySeq()
        .reduce(
            (reduction, id) =>
                reduction.set(id, selectedBillPayers.has(id) ? selectedBillPayers.get(id) : !isBatchInvoicingLimited),
            Map(),
        );
    return selectedBillPayerIdsFromPreview;
};

export default makeReducer<BatchInvoicing>(
    [
        match(Actions.showingInstitutionId, (_, {payload}) => {
            return new BatchInvoicing({
                institutionId: payload.institutionId,
            });
        }),
        match(Actions.selectBillPayer, (state, {payload}) => {
            return state.merge({
                selectedBillPayers: state.selectedBillPayers.set(payload.billPayerId, payload.select),
            });
        }),
        match(Actions.selectAllBillPayers, (state, {payload}) => {
            const billPayerIds = payload.billPayerIdsBeingShown;
            return state.merge({
                selectedBillPayers: Map(billPayerIds.map(id => [id, payload.select])),
            });
        }),
        match(Actions.selectBillPayerWithRadioButton, (state, {payload}) => {
            return state.merge({
                selectedBillPayers: Map(
                    state.selectedBillPayers.entrySeq().map(([key]) => {
                        if (key === payload.billPayerId) {
                            return [key, true];
                        }
                        return [key, false];
                    }),
                ),
            });
        }),
        match(Actions.showMoreWarnings, state => {
            return state.merge({
                warningsToShow: state.warningsToShow + warningsToShow,
            });
        }),
        match(Actions.fetchAllData, state => {
            return state.merge({
                previewInvoicesMap: Map(),
                updatingBillPayerIds: true,
                updatingInvoicePreviews: true,
            });
        }),
        match(Actions.fetchAllDataFailed, state => {
            return state.merge({
                updatingBillPayerIds: false,
                updatingInvoicePreviews: false,
            });
        }),
        match(Actions.fetchInvoicingBillPayersSuccess, (state, {payload}) =>
            state.merge({
                billPayerIds: payload.payload.billPayerIds,
                warnings: payload.payload.warnings,
                useGraphQl: payload.payload.useGraphQl,
                updatingBillPayerIds: false,
                didFetchBillPayerIds: true,
                invoices: payload.payload.invoices,
                behaviors: payload.payload.behaviors,
            }),
        ),
        match(Actions.previewBatchInvoiceFailed, state => state.merge({updatingInvoicePreviews: false})),
        match(Actions.previewBatchInvoiceSuccess, (state, {payload}) => {
            return state.merge({
                previewInvoicesMap: payload.payload.invoicePreviews,
                selectedBillPayers: getSelectedBillPayerIdsFromPreview(
                    state.selectedBillPayers,
                    payload.payload.invoicePreviews,
                    payload.payload.isBatchInvoicingLimited,
                ),
                updatingInvoicePreviews: false,
                didFetchInvoicePreviews: true,
            });
        }),
        match(Actions.batchInvoice, state => state.merge({sendingInvoices: true})),
        match(Actions.batchInvoiceSuccess, state => {
            return state.merge({
                sendingInvoices: false,
            });
        }),
        match(Actions.batchInvoiceFailed, state => state.merge({sendingInvoices: false})),
        match(Actions.batchInvoiceAborted, state => state.merge({sendingInvoices: false})),
    ],
    new BatchInvoicing(),
);
